import React, { FC } from 'react';
import { isFuture, parseISO } from 'date-fns';
import { majorScale, Pane, SymbolCircleIcon, Table } from 'evergreen-ui';
import { Action } from '../controllers/app.controller';
import { memberSearchFilter } from '../helpers/member-search-helper';
import { MemberGroup, SearchGroup } from '../models';
import { ActiveBadge } from './active-badge.component';
import { CoverageTierBadge } from './coverage-tier.component';

const columnStyles = [
  { flex: '1' },
  { flex: '0 0 180px' },
  { flex: '0 0 110px' },
  { flex: '1' },
  { flex: '0 0 110px' },
  { flex: '0 0 100px', justifyContent: 'center' },
];

export interface EmployeeListProps {
  memberGroups: MemberGroup[];
  selectedMemberGroupId?: string;
  dispatch: React.Dispatch<Action>;
  children?: never;
  searchKey?: string;
  searchGroup?: SearchGroup;
}

export const EmployeeList: FC<EmployeeListProps> = ({
  searchKey = '',
  searchGroup = SearchGroup.ALL,
  memberGroups,
  selectedMemberGroupId,
  dispatch,
}) => {
  const filteredMemberGroups = memberGroups.filter(({ primary, dependents }) => {
    return memberSearchFilter(primary, dependents, searchKey, searchGroup);
  });

  /* 
    NOTE: by default the childrenColumnName is "children". In our case, children refer to literal children.
    Setting the column name to something that intentionally doesn't exist disables this behavior.
  */
  return (
    <Table flex="1 0 100%">
      <Table.Head height={majorScale(5)}>
        <Table.TextHeaderCell {...columnStyles[0]}>Employee Name</Table.TextHeaderCell>
        <Table.TextHeaderCell {...columnStyles[1]}>Coverage Tier</Table.TextHeaderCell>
        <Table.TextHeaderCell {...columnStyles[2]}>Dependents</Table.TextHeaderCell>
        <Table.TextHeaderCell {...columnStyles[3]}>Plan</Table.TextHeaderCell>
        <Table.TextHeaderCell {...columnStyles[4]}>Status</Table.TextHeaderCell>
        <Table.TextHeaderCell {...columnStyles[5]} textProps={{ textAlign: 'center' }}>
          Issues
        </Table.TextHeaderCell>
      </Table.Head>
      <Table.VirtualBody height={`calc(100vh - 177px - 16px - var(--deprecation-banner-height, 0px))`}>
        {filteredMemberGroups.length ? (
          filteredMemberGroups.map(({ id, primary, coverageTier, size, benefitPlanName, errors, warnings }) => (
            <Table.Row
              key={id}
              height={majorScale(4)}
              isSelectable
              isSelected={id === selectedMemberGroupId}
              onSelect={() => dispatch({ type: 'selectMemberGroup', memberGroupId: id })}
              onDeselect={() => dispatch({ type: 'closeMemberGroup' })}
            >
              <Table.TextCell {...columnStyles[0]}>
                {primary.firstName} {primary.lastName}
              </Table.TextCell>

              <Table.Cell {...columnStyles[1]}>
                <CoverageTierBadge coverageTier={coverageTier} />
              </Table.Cell>

              <Table.TextCell isNumber {...columnStyles[2]}>
                {size - 1}
              </Table.TextCell>

              <Table.TextCell {...columnStyles[3]}>{benefitPlanName}</Table.TextCell>

              <Table.Cell {...columnStyles[4]}>
                <ActiveBadge
                  active={
                    (!primary.benefitEndDate || isFuture(parseISO(primary.benefitEndDate))) &&
                    !primary.isCancelTerminated
                  }
                />
              </Table.Cell>
              <Table.Cell {...columnStyles[5]}>
                {errors!.length > 0 && <SymbolCircleIcon color="danger" />}{' '}
                {warnings!.length > 0 && <SymbolCircleIcon color="warning" />}
              </Table.Cell>
            </Table.Row>
          ))
        ) : (
          <Pane display="flex" justifyContent="center" alignItems="center">
            No members found
          </Pane>
        )}
      </Table.VirtualBody>
    </Table>
  );
};
